/**
 * MULTI PL Total/combine: merge TAX Auto Adjust amounts across YOA by fsId.
 * Kept dependency-free for self-check (do not import services / icons here).
 */

export const TAX_AUTO_ADJ_TYPE = 'TAX';

export function isAutoAdjPaper(adj: any): boolean {
    return adj?.isAutoAdj === true || adj?.adjustmentMode === 4;
}

export function isTaxAutoAdj(adj: any): boolean {
    if (!isAutoAdjPaper(adj)) return false;
    return String(adj?.autoAdjType || '').trim() === TAX_AUTO_ADJ_TYPE;
}

export type MergedTaxAuto = {
    sum: number;
    /** Click target: prefer first non-zero header (align BalanceSheet merge). */
    representative: any;
};

/** Collect TAX Auto papers across period PL trees; sum by fsId. */
export function collectTaxAutoAdjustSumByFsId(
    periodTrees: any[][],
): Map<number, MergedTaxAuto> {
    const into = new Map<number, MergedTaxAuto>();
    const walk = (nodes: any[]) => {
        for (const n of nodes || []) {
            const children = Array.isArray(n?.children) ? n.children : [];
            if (children.length > 0) {
                walk(children);
                continue;
            }
            const fsId = Number(n?.fsId);
            if (!Number.isFinite(fsId) || fsId <= 0) continue;
            for (const a of Array.isArray(n?.adjustment) ? n.adjustment : []) {
                if (!isTaxAutoAdj(a)) continue;
                const amount = Number(a?.adjustment) || 0;
                const existing = into.get(fsId);
                if (!existing) {
                    into.set(fsId, {sum: amount, representative: {...a}});
                    continue;
                }
                existing.sum += amount;
                const repAmt = Math.abs(Number(existing.representative?.adjustment) || 0);
                if (repAmt < 0.01 && Math.abs(amount) >= 0.01) {
                    existing.representative = {...a};
                }
            }
        }
    };
    for (const tree of periodTrees || []) {
        walk(tree);
    }
    return into;
}

/** Replace per-YOA TAX Auto on leaves with one merged paper (sum + representative header). */
export function applyMergedTaxAutoOntoTree(
    nodes: any[] | null | undefined,
    taxByFsId: Map<number, MergedTaxAuto>,
): any[] {
    if (!nodes || nodes.length === 0) return nodes || [];
    if (!taxByFsId || taxByFsId.size === 0) return nodes;

    return nodes.map((n) => {
        const children = Array.isArray(n?.children) ? n.children : [];
        if (children.length > 0) {
            return {...n, children: applyMergedTaxAutoOntoTree(children, taxByFsId)};
        }
        const fsId = Number(n?.fsId);
        const merged = Number.isFinite(fsId) ? taxByFsId.get(fsId) : undefined;
        if (!merged) return n;

        const otherAdjs = (Array.isArray(n?.adjustment) ? n.adjustment : []).filter(
            (a: any) => !isTaxAutoAdj(a),
        );
        const mergedAdj = {
            ...merged.representative,
            adjustment: merged.sum,
            isAutoAdj: true,
            autoAdjType: TAX_AUTO_ADJ_TYPE,
            adjustmentMode: merged.representative?.adjustmentMode ?? 4,
        };
        return {...n, adjustment: [...otherAdjs, mergedAdj]};
    });
}
